import { useCallback, useEffect, useRef, useState } from "react";
import {
  CONFIRM_PRESCRIPTION_CHOICES,
  CONFIRM_PRESCRIPTION_CTA,
  EXISTING_PRESCRIPTION_VALID_UNTIL_LABEL,
  PRESCRIPTION_CHOICE_LABEL,
  PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL,
  PRESCRIPTION_NOT_REGISTERED_LABEL,
  prescriptionLabel,
  type ConfirmPrescriptionChoice,
  type TreatmentStartView,
} from "@care-by-ella/contracts";
import { CLINICAL_PHASE_LABEL } from "./ClinicalPhaseField";
import { useReadOnly } from "./SupportView";
import { ForbiddenError, UnauthorizedError } from "@/lib/clinician-api";
import { errorText } from "@/lib/error-text";
import {
  AWAITING_ACTIVATION_HINT,
  canConfirmPrescription,
  PRESCRIPTION_STEP_HINT,
  showsAwaitingActivation,
  showsConfirmPrescription,
  showsPendingPrescriptionStep,
  TREATMENT_START_ANCHOR,
} from "@/lib/treatment-start";
import {
  confirmPrescription,
  fetchTreatmentStart,
} from "@/lib/treatment-start-api";

type Choice = (typeof CONFIRM_PRESCRIPTION_CHOICES)[number];

// TES-1070 (TES-1069 S1) — the doctor's prescription step, in the patient's
// treatment area on "Översikt" (and "Receptövertag").
//
// TES-1150 (Emelie 2026-10-07) — part of ONE session with the decision, and
// open from the published positive decision, paid or not:
// "Kirill ska … utfärda receptet i Webdoc eller bekräfta att ett befintligt giltigt recept fortfarande gäller, bekräfta receptstatusen i admin."
// One choice and one button, "Bekräfta receptstatus". TES-1141's locked
// step ("Abonnemanget är ännu inte aktiverat. Recept får inte utfärdas.") and
// TES-1080's separate prescription note are gone: the decision's journal
// note is the session's note. After the registration the panel reads
// "Recept utfärdat – inväntar abonnemangsaktivering" until the verified
// subscription releases the prescription to the patient.
//
// The `not_yet` choice is not offered here (Emelie's open question 3 in
// TES-1069): a doctor who cannot issue the prescription yet leaves the step
// open, and the work item "Behandlingsbeslut publicerat – bekräfta
// receptstatus" stays.
export function TreatmentStartPanel({
  patientId,
  onUnauthorized,
  onConfirmed,
}: {
  patientId: string;
  onUnauthorized: () => void;
  onConfirmed: () => void;
}) {
  // TES-1040 — the admin's read-only support view sees the status, never
  // the controls.
  const readOnly = useReadOnly();
  const [view, setView] = useState<TreatmentStartView | null>(null);
  const [choice, setChoice] = useState<Choice | null>(null);
  const [validUntil, setValidUntil] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // The parent passes a fresh callback each render; a ref keeps it out of
  // the load's dependencies so a re-render never refetches.
  const unauthorized = useRef(onUnauthorized);
  unauthorized.current = onUnauthorized;

  const load = useCallback(async () => {
    try {
      setView(await fetchTreatmentStart(patientId));
    } catch (e) {
      if (e instanceof UnauthorizedError) unauthorized.current();
      // A role without access (403) or an older server (404) has no panel.
      else if (!(e instanceof ForbiddenError)) setView(null);
    }
  }, [patientId]);

  useEffect(() => {
    setView(null);
    setChoice(null);
    setValidUntil("");
    setError(null);
    void load();
  }, [load]);

  const shown = showsConfirmPrescription(view?.state, readOnly);

  // Arriving from the work item: bring the panel into view.
  useEffect(() => {
    if (!shown) return;
    if (window.location.hash.slice(1) === TREATMENT_START_ANCHOR) {
      document
        .getElementById(TREATMENT_START_ANCHOR)
        ?.scrollIntoView({ block: "start" });
    }
  }, [shown]);

  if (!view) return null;
  if (showsAwaitingActivation(view.state)) {
    return <AwaitingActivation view={view} />;
  }
  if (!showsPendingPrescriptionStep(view.state)) return null;
  if (!shown) {
    // The read-only support view: the step is open, and it is the doctor's.
    return (
      <section
        id={TREATMENT_START_ANCHOR}
        className="flex flex-col gap-xs rounded-md border border-surfaceMuted bg-surface p-md"
      >
        <h2 className="font-display text-lg">Behandlingsstart</h2>
        <p className="text-sm">{PRESCRIPTION_NOT_REGISTERED_LABEL}</p>
      </section>
    );
  }

  const prescription: ConfirmPrescriptionChoice | null = choice
    ? {
        choice,
        ...(choice === "existing_valid" && validUntil
          ? { existingValidUntil: validUntil }
          : {}),
      }
    : null;

  const submit = async () => {
    if (!prescription) return;
    setBusy(true);
    setError(null);
    try {
      const result = await confirmPrescription(patientId, { prescription });
      setView(result.view);
      onConfirmed();
    } catch (e) {
      if (e instanceof UnauthorizedError) unauthorized.current();
      else setError(errorText(e instanceof Error ? e.message : String(e)));
    } finally {
      setBusy(false);
    }
  };

  const order = view.plannedOrder;
  return (
    <section
      id={TREATMENT_START_ANCHOR}
      aria-labelledby="treatment-start-title"
      className="flex flex-col gap-md rounded-md border border-brand/40 bg-surface p-md"
    >
      <div>
        <h2 id="treatment-start-title" className="font-display text-lg">
          Behandlingsstart – receptstatus
        </h2>
        <p className="text-sm text-textSecondary">{PRESCRIPTION_STEP_HINT}</p>
      </div>
      <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-xs text-sm">
        <dt className="text-textSecondary">Planerad ordination</dt>
        <dd>
          {order
            ? prescriptionLabel({
                medication: order.medication,
                dose: order.dose,
                frequency: order.frequency,
                duration: order.duration,
              })
            : "Saknas"}
        </dd>
        <dt className="text-textSecondary">Fas</dt>
        <dd>
          {view.clinicalPhase
            ? CLINICAL_PHASE_LABEL[view.clinicalPhase]
            : "Saknas"}
        </dd>
      </dl>
      <fieldset className="flex flex-col gap-xs" disabled={busy}>
        <legend className="mb-xs text-sm font-medium">Recept i Webdoc</legend>
        {CONFIRM_PRESCRIPTION_CHOICES.map((c) => (
          <label key={c} className="flex items-center gap-sm text-sm">
            <input
              type="radio"
              name={`treatment-start-choice-${patientId}`}
              value={c}
              checked={choice === c}
              onChange={() => setChoice(c)}
            />
            {PRESCRIPTION_CHOICE_LABEL[c]}
          </label>
        ))}
        {choice === "existing_valid" ? (
          <label className="ml-lg flex flex-col gap-xs text-sm">
            <span className="text-textSecondary">
              {EXISTING_PRESCRIPTION_VALID_UNTIL_LABEL} (om känt)
            </span>
            <input
              type="date"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              className="w-fit rounded-md border border-surfaceMuted p-xs text-sm"
            />
          </label>
        ) : null}
      </fieldset>
      {error ? (
        <p
          role="alert"
          className="rounded-md border border-danger/30 bg-danger/10 p-sm text-sm text-danger"
        >
          {error}
        </p>
      ) : null}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void submit()}
          disabled={!canConfirmPrescription({ choice, busy })}
          className="rounded-md bg-brand px-md py-sm text-sm text-textOnBrand hover:opacity-90 disabled:opacity-60"
        >
          {busy ? "Bekräftar…" : CONFIRM_PRESCRIPTION_CTA}
        </button>
      </div>
    </section>
  );
}

// TES-1150 — after the registration: the status, what was registered and
// when, and that nothing is left for the doctor. Shown in the read-only
// support view too (it carries no actions).
function AwaitingActivation({ view }: { view: TreatmentStartView }) {
  const reg = view.registration ?? null;
  const order = view.plannedOrder;
  return (
    <section
      id={TREATMENT_START_ANCHOR}
      aria-labelledby="treatment-start-awaiting-title"
      className="flex flex-col gap-sm rounded-md border border-surfaceMuted bg-surface p-md"
    >
      <h2 id="treatment-start-awaiting-title" className="font-display text-lg">
        {PRESCRIPTION_ISSUED_AWAITING_SUBSCRIPTION_LABEL}
      </h2>
      <dl className="grid grid-cols-[auto_1fr] gap-x-md gap-y-xs text-sm">
        {order ? (
          <>
            <dt className="text-textSecondary">Ordination</dt>
            <dd>
              {prescriptionLabel({
                medication: order.medication,
                dose: order.dose,
                frequency: order.frequency,
                duration: order.duration,
              })}
            </dd>
          </>
        ) : null}
        {reg ? (
          <>
            <dt className="text-textSecondary">Receptstatus</dt>
            <dd>
              {PRESCRIPTION_CHOICE_LABEL[reg.choice]}
              {reg.existingValidUntil
                ? ` (${EXISTING_PRESCRIPTION_VALID_UNTIL_LABEL.toLowerCase()} ${reg.existingValidUntil})`
                : ""}
            </dd>
            <dt className="text-textSecondary">Bekräftad</dt>
            <dd>{reg.registeredAt.slice(0, 10)}</dd>
          </>
        ) : null}
      </dl>
      <p className="text-sm text-textSecondary">{AWAITING_ACTIVATION_HINT}</p>
    </section>
  );
}
